<template>
    <CompassTableRow>
        <CompassTableBodyCell maxWidth="110px" minWidth="110px">
            <CompassFormItem label="" :error="taxRateIdError">
                <CompassSelect
                    v-model="taxRateId"
                    class="ocr-tax-line-row__input"
                    :disabled="props.disabled"
                    filterable
                    :placeholder="$t.__('ocr.OcrVerificationForm.select_rate')"
                    :error="!!taxRateIdError"
                >
                    <CompassDropdownOption
                        v-for="rate in compliantTaxRates"
                        :key="rate.id"
                        :label="taxLabel(rate)"
                        :value="rate.id"
                    />
                </CompassSelect>
            </CompassFormItem>
        </CompassTableBodyCell>
        <CompassTableBodyCell maxWidth="200px" minWidth="200px">
            <CompassFormItem label="" :error="descriptionError">
                <CompassInput
                    v-model="description"
                    class="ocr-tax-line-row__input"
                    :disabled="props.disabled"
                    :error="!!descriptionError"
                />
            </CompassFormItem>
        </CompassTableBodyCell>
        <CompassTableBodyCell maxWidth="140px" minWidth="140px">
            <CompassFormItem label="" :error="amountExcludingTaxError">
                <CompassInput
                    v-model="amountExcludingTax"
                    class="ocr-tax-line-row__input"
                    :disabled="props.disabled"
                    :error="!!amountExcludingTaxError"
                    inputmode="decimal"
                    @update:modelValue="onAmountExcludingTaxInput"
                    @blur="formatAmountExcludingTaxOnBlur"
                >
                    <template v-if="props.currencySymbol" #suffix>
                        {{ props.currencySymbol }}
                    </template>
                </CompassInput>
            </CompassFormItem>
        </CompassTableBodyCell>
        <CompassTableBodyCell maxWidth="140px" minWidth="140px">
            <CompassFormItem label="" :error="amountIncludingTaxError">
                <CompassInput
                    v-model="amountIncludingTax"
                    class="ocr-tax-line-row__input"
                    :disabled="props.disabled"
                    :error="!!amountIncludingTaxError"
                    inputmode="decimal"
                    @update:modelValue="onAmountIncludingTaxInput"
                    @blur="formatAmountIncludingTaxOnBlur"
                >
                    <template v-if="props.currencySymbol" #suffix>
                        {{ props.currencySymbol }}
                    </template>
                </CompassInput>
            </CompassFormItem>
        </CompassTableBodyCell>
        <CompassTableBodyCell maxWidth="140px" minWidth="140px">
            <CompassSelect
                v-model="unit"
                class="ocr-tax-line-row__input"
                :disabled="props.disabled"
                filterable
                :placeholder="$t.__('ocr.OcrVerificationProductsTable.select_unit')"
            >
                <CompassDropdownOption :value="null" :label="$t.__('common.labels.none')" />
                <CompassDropdownOption
                    v-for="unitItem in props.units"
                    :key="unitItem.id"
                    :label="unitItem.label"
                    :value="unitItem.id"
                />
            </CompassSelect>
        </CompassTableBodyCell>
        <CompassTableBodyCell maxWidth="200px" minWidth="200px">
            <CompassFormItem label="" :error="accountingCodeError">
                <CompassSelect
                    v-model="accountingCode"
                    class="ocr-tax-line-row__input"
                    :disabled="props.disabled"
                    filterable
                    :placeholder="$t.__('ocr.OcrVerificationForm.select_code')"
                    :error="!!accountingCodeError"
                >
                    <CompassDropdownOption :value="null" :label="$t.__('common.labels.none')" />
                    <CompassDropdownOption
                        v-for="item in props.accountingCodes"
                        :key="item.id"
                        :label="`${item.code} - ${item.label}`"
                        :value="item.id"
                    />
                </CompassSelect>
            </CompassFormItem>
        </CompassTableBodyCell>
        <CompassTableBodyCell v-if="props.showAnalyticCode" maxWidth="200px" minWidth="200px">
            <CompassInput
                v-model="analyticCode"
                class="ocr-tax-line-row__input"
                :disabled="props.disabled"
                :placeholder="$t.__('ocr.OcrVerificationProductsTable.analytic_code')"
            />
        </CompassTableBodyCell>
        <CompassTableBodyCell idColumn="delete">
            <CompassTooltip
                v-if="props.canDelete"
                :content="$t.__('common.actions.delete')"
                :arrow="false"
            >
                <CompassIconButton
                    icon="bin"
                    :disabled="props.disabled"
                    :variant="CompassEnums.IconButtonVariant.DESTRUCTIVE"
                    :size="CompassEnums.IconButtonSize.MEDIUM"
                    @click="emit('request-delete', props.index)"
                />
            </CompassTooltip>
        </CompassTableBodyCell>
    </CompassTableRow>
</template>

<script setup lang="ts">
import { computed, inject, watch, type ComputedRef, type Ref } from 'vue';
import { useField, useSubmitCount } from 'vee-validate';
import {
    limitToTwoDecimals,
    formatAmountToTwoDecimals,
    calculateAmountIncludingTax,
} from 'composables/useOcrProducts';
import { useTax } from 'composables/useTax';
import type { TaxRate } from 'types/entities/accounting/taxRate.type';
import type { AccountingCode } from 'types/entities/accounting/accountingCode.type';
import type { Unit } from 'types/entities/units/unit.type';
import type { Translator } from 'types/injects/translator.type';

interface Props {
    index: number;
    disabled?: boolean;
    canDelete?: boolean;
    taxRates: TaxRate[];
    accountingCodes: AccountingCode[];
    units?: Unit[];
    showAnalyticCode?: boolean;
    currencySymbol?: string;
}

const props = withDefaults(defineProps<Props>(), {
    disabled: false,
    canDelete: false,
    units: () => [],
    showAnalyticCode: false,
    currencySymbol: undefined,
});

const emit = defineEmits<{
    (event: 'request-delete', index: number): void;
}>();

const $t = inject('$t') as Translator;
const { taxLabel, isEinvoicingCompliant } = useTax();

const submitCount = useSubmitCount();
const fieldOptions = { validateOnValueUpdate: false };
const fieldPath =
    (name: string): (() => string) =>
    () =>
        `taxLines[${props.index}].${name}`;

const gateError = (message: Ref<string | undefined>): ComputedRef<string> =>
    computed(() => (submitCount.value > 0 ? (message.value ?? '') : ''));

const { value: taxRateId, errorMessage: taxRateIdMessage } = useField<number | null>(
    fieldPath('taxRateId'),
    undefined,
    fieldOptions,
);
const taxRateIdError = gateError(taxRateIdMessage);

const { value: description, errorMessage: descriptionMessage } = useField<string>(
    fieldPath('description'),
    undefined,
    fieldOptions,
);
const descriptionError = gateError(descriptionMessage);

const { value: amountIncludingTax, errorMessage: amountIncludingTaxMessage } = useField<
    number | string | null
>(fieldPath('amountIncludingTax'), undefined, fieldOptions);
const amountIncludingTaxError = gateError(amountIncludingTaxMessage);

const { value: amountExcludingTax, errorMessage: amountExcludingTaxMessage } = useField<
    number | string | null
>(fieldPath('amountExcludingTax'), undefined, fieldOptions);
const amountExcludingTaxError = gateError(amountExcludingTaxMessage);

const { value: unit } = useField<string | number | null>(
    fieldPath('unit'),
    undefined,
    fieldOptions,
);

const { value: accountingCode, errorMessage: accountingCodeMessage } = useField<number | null>(
    fieldPath('accountingCode'),
    undefined,
    fieldOptions,
);
const accountingCodeError = gateError(accountingCodeMessage);

const { value: analyticCode } = useField<string | null>(
    fieldPath('analyticCode'),
    undefined,
    fieldOptions,
);

const compliantTaxRates = computed<TaxRate[]>(() =>
    props.taxRates.filter((tax) => isEinvoicingCompliant(tax)),
);

const onAmountIncludingTaxInput = (value: string | number | null): void => {
    amountIncludingTax.value = limitToTwoDecimals(value);
};

const formatAmountIncludingTaxOnBlur = (): void => {
    amountIncludingTax.value = formatAmountToTwoDecimals(amountIncludingTax.value);
};

const onAmountExcludingTaxInput = (value: string | number | null): void => {
    amountExcludingTax.value = limitToTwoDecimals(value);
};

const formatAmountExcludingTaxOnBlur = (): void => {
    amountExcludingTax.value = formatAmountToTwoDecimals(amountExcludingTax.value);
};

watch([amountExcludingTax, taxRateId], () => {
    const taxItem = props.taxRates.find((item) => item.id === taxRateId.value);
    if (!taxItem) {
        return;
    }
    const includingTax = calculateAmountIncludingTax(
        amountExcludingTax.value,
        Number(taxItem.rate),
    );
    if (includingTax !== null) {
        amountIncludingTax.value = formatAmountToTwoDecimals(includingTax);
    }
});
</script>

<style lang="scss" scoped>
:deep(.compass-table__cell-container) {
    width: 100%;
}

.ocr-tax-line-row {
    &__input {
        width: 100%;
    }
}

:deep(.compass-table__cell) {
    vertical-align: top;
}

:deep(.compass-form-item) {
    width: 100%;

    .compass-form-item__caption-container,
    .compass-form-item__caption {
        width: 100%;
    }

    .compass-form-item__caption {
        white-space: normal;
    }
}
</style>
